/* eslint-disable no-undef */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  CardActionArea,
  Button,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import { brandColors } from '../../theme';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';
import { useUserEntitlements } from '../../hooks/useUserEntitlements';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { 
  LocationOn as MarketIcon,
  Flag as CountryIcon,
  Public as ContinentIcon,
  ManageAccounts as ManageIcon,
  TrendingUp,
  Subscriptions as SubscriptionsIcon,
  PriorityHigh as PriorityHighIcon,
} from '@mui/icons-material';
import { API_URL } from '../../utils/config';

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [marketSearchTerm, setMarketSearchTerm] = useState('');
  const [trendReports, setTrendReports] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const hasInitializedTrendReports = useRef(false);
  
  // Memoize filters to prevent infinite loop - include both active and expiring
  const filters = useMemo(() => ({}), []);
  
  const { 
    data: entitlementsData, 
    loading: entitlementsLoading, 
    error: entitlementsError 
  } = useUserEntitlements({
    filters,
    enabled: !!currentUser,
  });

  // Group entitlements by access level - only show active and expiring
  const validStatuses = ['active', 'expiring'];
  const marketEntitlements = entitlementsData?.entitlements.filter(e => 
    e.accessLevel === 'market' && validStatuses.includes(e.status)
  ) || [];
  
  // Sort market entitlements alphabetically by market name
  const sortedMarketEntitlements = useMemo(() => {
    return [...marketEntitlements].sort((a, b) => {
      const nameA = a.market?.name || '';
      const nameB = b.market?.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [marketEntitlements]);
  
  // Filter market entitlements based on search term
  const filteredMarketEntitlements = useMemo(() => {
    if (!marketSearchTerm.trim()) {
      return sortedMarketEntitlements;
    }
    
    const searchLower = marketSearchTerm.toLowerCase().trim();
    return sortedMarketEntitlements.filter(entitlement => {
      const marketName = entitlement.market?.name?.toLowerCase() || '';
      const countryName = entitlement.market?.countryName?.toLowerCase() || '';
      return marketName.includes(searchLower) || countryName.includes(searchLower);
    });
  }, [sortedMarketEntitlements, marketSearchTerm]);
  
  const countryEntitlements = entitlementsData?.entitlements.filter(e => 
    e.accessLevel === 'country' && validStatuses.includes(e.status)
  ) || [];
  const continentEntitlements = entitlementsData?.entitlements.filter(e => 
    e.accessLevel === 'continent' && validStatuses.includes(e.status)
  ) || [];

  // Fetch Custom Comp Set Reports only once when user is authenticated
  React.useEffect(() => {
    if (!currentUser || hasInitializedTrendReports.current) {
      return;
    }
    
    hasInitializedTrendReports.current = true;
    
    const fetchTrendReports = async () => {
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/store/trend-reports`,
          { method: 'GET' }
        );
        
        if (response?.ok) {
          const data = await response.json();
          setTrendReports(data.purchases || []);
        }
      } catch (error) {
        console.error('Error fetching Custom Comp Set Reports:', error);
      } finally {
        setLoadingReports(false);
      }
    };
    
    fetchTrendReports();
  }, [currentUser, authenticatedFetch]);

  // Handle hash changes and permission checks
  useEffect(() => {
    // Check if hash indicates Custom Comp Set Reports tab
    if (location.hash === '#trend-reports' && currentUser?.permissions?.includes('store.privileged')) {
      setTabValue(1);
    } else if (location.hash === '#trend-reports' && currentUser && !currentUser?.permissions?.includes('store.privileged')) {
      // User doesn't have permission, reset to subscriptions tab
      setTabValue(0);
      // Remove the hash since they can't access it
      window.location.hash = '';
    }
  }, [location.hash, currentUser]);
  
  // Reset tab to 0 if Custom Comp Set Reports tab is selected but user doesn't have permission
  React.useEffect(() => {
    if (tabValue === 1 && !currentUser?.permissions?.includes('store.privileged')) {
      setTabValue(0);
    }
  }, [tabValue, currentUser]);

  const handleManageSubscriptions = async () => {
    setIsLoadingPortal(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/store/customerPortal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response && response.ok) {
        const data = await response.json();
        if (data.portalUrl) {
          window.location.href = data.portalUrl;
        }
      } else {
        // Failed to get customer portal URL
      }
    } catch (_error) {
      void _error;
      // Failed to get customer portal URL
    } finally {
      setIsLoadingPortal(false);
    }
  };


  // No longer redirect to access-denied - just show empty subscriptions page

  // If user is not loaded yet, show loading
  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </Container>
    );
  }


  const renderMarketCard = (entitlement: any) => {
    const market = entitlement.market;
    if (!market) return null;
    
    
    const formatDate = (date: string | Date | undefined) => {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    };

    const formatBillingPeriod = (period?: string) => {
      if (!period) return 'N/A';
      return period.charAt(0).toUpperCase() + period.slice(1);
    };

    return (
    <Grid item xs={12} sm={6} md={4} lg={3} key={market.id}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          }
        }}
      >
        <CardActionArea 
          onClick={() => navigate(`/market/${market.id}`)}
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          {market.productImage ? (
            <Box sx={{ height: 200, position: 'relative', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
              <img
                src={market.productImage}
                alt={market.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  // Show fallback icon when image fails to load
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.display = 'flex';
                    parent.style.alignItems = 'center';
                    parent.style.justifyContent = 'center';
                    parent.style.backgroundColor = '#f5f5f5';
                    parent.innerHTML = '<svg style="font-size: 60px; color: #9e9e9e;" class="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"></path></svg>';
                  }
                }}
              />
            </Box>
          ) : (
            <Box 
              sx={{ 
                height: 200, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: 'grey.100'
              }}
            >
              <MarketIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
            </Box>
          )}
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" gutterBottom>
              {market.name}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              {market.countryName && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Location:</strong> {market.countryName}
                </Typography>
              )}
              
              {entitlement.billingPeriod && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Billing:</strong> {formatBillingPeriod(entitlement.billingPeriod)}
                </Typography>
              )}
              
              <Typography 
                variant="body2" 
                sx={{
                  color: entitlement.status === 'expiring' ? "rgb(196, 22, 28)" : "text.secondary"
                }}
              >
                {(() => {
                  if (entitlement.status === 'expiring') {
                    return <><strong>Expiring:</strong> {formatDate(entitlement.validUntil)}</>;
                  }
                  
                  if (entitlement.status === 'active' && entitlement.currentPeriodEnd) {
                    return <><strong>Renews:</strong> {formatDate(entitlement.currentPeriodEnd)}</>;
                  }
                  
                  if (!entitlement.validUntil) {
                    return <><strong>Active:</strong> Lifetime</>;
                  }
                  
                  return <><strong>Renews:</strong> {formatDate(entitlement.validUntil)}</>;
                })()}
              </Typography>
            </Box>
            
            {entitlement.status === 'active' && (
              <Typography variant="caption" color="primary" sx={{ mt: 1.5, display: 'block' }}>
                Click to access reports and podcasts
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Box sx={{ mb: 3, mt: 2 }}>
          <Typography
            component="h1"
            sx={{
              color: brandColors.blue,
              fontWeight: 400,
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.5px',
            }}
          >
            Your Purchases
          </Typography>
        </Box>
        
        {/* Always show tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={(event, newValue) => {
              setTabValue(newValue);
              // Update URL hash based on selected tab
              if (newValue === 1) {
                window.location.hash = 'trend-reports';
              } else {
                window.location.hash = '';
              }
            }}
            aria-label="purchase tabs"
          >
            <Tab 
              label="MarketCompass Subscriptions" 
              icon={<SubscriptionsIcon />} 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            {currentUser?.permissions?.includes('store.privileged') && (
              <Tab 
                label="Custom Comp Set Reports" 
                icon={<TrendingUp />} 
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            )}
          </Tabs>
        </Box>

        {/* MarketCompass Subscriptions Tab */}
        {tabValue === 0 && (
          <Box sx={{ mt: 3 }}>
            {entitlementsLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading your subscriptions...</Typography>
              </Box>
            ) : entitlementsError ? (
              <Alert severity="error">
                Failed to load subscriptions. Please try again later.
              </Alert>
            ) : (
              <>
                {entitlementsData?.entitlements.length === 0 && (
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      You don't have any active subscriptions yet.
                    </Typography>
                  </Paper>
                )}

            {/* Direct Market Subscriptions */}
            {marketEntitlements.length > 0 && (
              <Box sx={{ mt: 3 }}>
                {/* Search bar - show only when there are more than 8 market subscriptions */}
                {marketEntitlements.length > 8 && (
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search market subscriptions..."
                      value={marketSearchTerm}
                      onChange={(e) => setMarketSearchTerm(e.target.value)}
                      sx={{ maxWidth: 400 }}
                    />
                  </Box>
                )}
                
                <Grid container spacing={3}>
                  {filteredMarketEntitlements.map(entitlement => (
                    entitlement.market && renderMarketCard(entitlement)
                  ))}
                </Grid>
                
                {/* Show message when search has no results */}
                {marketSearchTerm && filteredMarketEntitlements.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No market subscriptions found matching "{marketSearchTerm}"
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Country-level Subscriptions */}
            {countryEntitlements.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CountryIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Country Subscriptions
                    </Typography>
                    <Chip 
                      label={countryEntitlements.length} 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Box>
                  {countryEntitlements.map(entitlement => (
                    <Box key={entitlement.id} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {entitlement.country?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Access to all markets in this country
                      </Typography>
                    </Box>
                  ))}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Country subscriptions give you access to all markets within the country. 
                    Visit the Market Reports page to explore available markets.
                  </Alert>
                </CardContent>
              </Card>
            )}

            {/* Continent-level Subscriptions */}
            {continentEntitlements.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ContinentIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Continent Subscriptions
                    </Typography>
                    <Chip 
                      label={continentEntitlements.length} 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Box>
                  {continentEntitlements.map(entitlement => (
                    <Box key={entitlement.id} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {entitlement.continent?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Access to all markets in this continent
                      </Typography>
                    </Box>
                  ))}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Continent subscriptions give you access to all markets within the continent. 
                    Visit the Market Reports page to explore available markets.
                  </Alert>
                </CardContent>
              </Card>
            )}

                {/* Manage Subscriptions Button */}
                {entitlementsData && entitlementsData.entitlements.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Button
                      variant="contained"
                      startIcon={<ManageIcon />}
                      onClick={handleManageSubscriptions}
                      disabled={isLoadingPortal}
                      fullWidth
                      size="large"
                    >
                      {isLoadingPortal ? 'Loading...' : 'Manage Subscriptions'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Custom Comp Set Reports Tab */}
        {tabValue === 1 && (
          <Box sx={{ mt: 3 }}>
              
                {loadingReports ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : trendReports.length === 0 ? (
                  <Paper sx={{ p: 3 }}>
                    <Typography color="text.secondary">
                      No Custom Comp Set Reports purchased yet
                    </Typography>
                  </Paper>
                ) : (
                <Grid container spacing={3}>
                  {trendReports.map((report) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={report.id}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                          }
                        }}
                      >
                        <CardActionArea 
                          onClick={() => navigate(`/trendreport/${report.id}`)}
                          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                        >
                          <Box sx={{ height: 200, position: 'relative', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                            <img
                              src="/images/trend_report_950x720.jpg"
                              alt="Custom Comp Set Report"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: 'block'
                              }}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                const target = e.currentTarget;
                                target.style.display = 'none';
                                // Show fallback icon when image fails to load
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.style.display = 'flex';
                                  parent.style.alignItems = 'center';
                                  parent.style.justifyContent = 'center';
                                  parent.style.backgroundColor = '#f5f5f5';
                                  parent.innerHTML = '<svg style="font-size: 60px; color: #9e9e9e;" class="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M 6 2 C 4.9 2 4.01 2.9 4.01 4 L 4 20 c 0 1.1 0.89 2 1.99 2 H 18 c 1.1 0 2 -0.9 2 -2 V 8 l -6 -6 H 6 z m 7 7 V 3.5 L 18.5 9 H 13 z"/></svg>';
                                }
                              }}
                            />
                          </Box>
                          <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" component="div" gutterBottom>
                              {report.marketName}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Type:</strong> Custom Comp Set Report
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary">
                                <strong>Purchased:</strong> {new Date(report.purchaseDate).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </Typography>
                              
                              <Box sx={{ mt: 1 }}>
                                <Chip 
                                  label={report.status.replace(/_/g, ' ').charAt(0).toUpperCase() + report.status.replace(/_/g, ' ').slice(1)} 
                                  color={
                                    report.status === 'purchased' ? 'error' :
                                    report.status === 'completed' ? 'success' :
                                    report.status === 'submitted' ? 'warning' :
                                    report.status === 'needs_adjustments' ? 'error' :
                                    'default'
                                  }
                                  size="small"
                                />
                              </Box>
                            </Box>
                            
                            {report.status === 'purchased' && (
                              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PriorityHighIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                <Typography variant="caption" color="error.main" sx={{ fontStyle: 'italic' }}>
                                  Additional information required
                                </Typography>
                              </Box>
                            )}
                            
                            {report.status === 'completed' && (
                              <Typography variant="caption" color="primary" sx={{ mt: 1.5, display: 'block' }}>
                                Click to view and download your report
                              </Typography>
                            )}
                            
                            {report.status === 'submitted' && (
                              <Typography variant="caption" color="info.main" sx={{ mt: 1.5, display: 'block' }}>
                                Your report is being processed
                              </Typography>
                            )}
                            
                            {report.status === 'needs_adjustments' && (
                              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PriorityHighIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                <Typography variant="caption" color="error.main">
                                  Adjustments needed - click for details
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                  </Grid>
                )}
              </Box>
            )}
      </Box>
    </Container>
  );
};

export default SubscriptionsPage;